import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Grid,
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  Paper,
  Tooltip,
  Badge,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import BackdropComponent from "../../../Spinner/Spinner";
import TextLabel from "components/Common/TextLabel/TextLabel";
import classes from "../../OMTBulkEdit/Common.module.css";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import {
  CustomButton,
  CustomTooltip,
} from "../../../../components/RosterConfig/RosterConfigStyle";
import SnackBar from "../../../../components/SnackBar/SnackBar";
import NetworkCheckOutlinedIcon from "@mui/icons-material/NetworkCheckOutlined";
import WifiOffOutlinedIcon from "@mui/icons-material/WifiOffOutlined";
import { fontStyles } from "components/Common/commonStyles";

import {
  getOMTNetwork,
  saveOMTNetwork,
} from "../../../../services/userServices";
import { AttachFile } from "@mui/icons-material";
import OMTAttachments from "../OMTAttachments";
import { red } from "@mui/material/colors";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useColumnFilter } from "../useColumnFilter";
import ColumnFilterPopover from "../ColumnFilterPopover";

const isLockedByHardware = (row) => {
  const reason = row.HardwareFailureReason;
  if (!reason) return false;
  if (reason.startsWith("Pre-pass")) return false;
  return true;
};

const Network = forwardRef(function Network(props, ref) {
  const {
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    editAccess,
    agentOracleId,
    onDirtyChange,
    noRosterIdClassName,
    isUploadMode,
    isNoRosterIdSearch,
  } = props;
  const [loading, setLoader] = useState(false);
  const [originalNetworkData, setOriginalNetworkData] = useState([]);
  const [order, setOrder] = useState("ASC");
  const [NetworkData, setNetworkData] = useState([]);

  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const isFirstRender = useRef(true);

  const handleAttachmentOpen = (row) => {
    setOpenAttachmentDialog(true);
    setSelectedRow(row);
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const isDirty =
    JSON.stringify(NetworkData) !== JSON.stringify(originalNetworkData);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);
  useImperativeHandle(ref, () => ({ save: () => handleSave() }));

  useEffect(() => {
    let cancelled = false;

    const loadNetworkData = async () => {
      if (
        (rosterId !== null && rosterId !== "") ||
        (noRosterIdClassName !== null && noRosterIdClassName !== "") ||
        (agentOracleId !== null && agentOracleId !== "" && !noRosterIdClassName)
      ) {
        if (!cancelled) {
          setNetworkData([]);
        }

        try {
          await fetchNetworkData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to fetch network data:", error);
          }
        }
      } else {
        setNetworkData([]);
      }
    };

    loadNetworkData();

    return () => {
      cancelled = true;
    };
  }, [rosterId, agentOracleId, noRosterIdClassName]);

  useEffect(() => {
    let cancelled = false;

    const refreshAfterAttachment = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return; // Skip first render
      }

      if (openAttachmentDialog === false) {
        try {
          await fetchNetworkData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to refresh network data:", error);
        }
      }
    };

    refreshAfterAttachment();

    return () => {
      cancelled = true;
    };
  }, [openAttachmentDialog]);

  const fetchNetworkData = async (
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    agentOracleId,
    noRosterIdClassName,
    isNoRosterIdSearch,
  ) => {
    setLoader(true);
    try {
      const response = await getOMTNetwork(
        rosterId,
        asdLeadOracleId,
        taskTypeId,
        scheduleType,
        agentOracleId,
        noRosterIdClassName,
        isNoRosterIdSearch,
      );

      if (response?.data?.message === "Success") {
        setNetworkData(response.data.data);
        setOriginalNetworkData(JSON.parse(JSON.stringify(response.data.data)));
      } else {
        setNetworkData([]);
        setOriginalNetworkData([]);
      }
    } catch (error) {
      const is404 =
        error?.response?.status === 404 || error?.statusCode === 404;
      if (is404) {
        setNetworkData([]);
        setOriginalNetworkData([]);
        if (rosterId || noRosterIdClassName) {
          setSnack({
            open: true,
            message: "No network records found for the selected filters.",
            severity: "info",
          });
        }
      } else {
        setSnack({
          open: true,
          message: "Error fetching network data",
          severity: "error",
        });
      }
    }
    setLoader(false);
  };

  const handleChangeChecked = (event, rowIndex) => {
    let trainee = NetworkData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [event.target.name]: event.target.checked,
        };
      } else {
        return item;
      }
    });

    setNetworkData(trainee);
  };

  const handleTextValueChange = (event, rowIndex, fieldName) => {
    let trainee = NetworkData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [fieldName]: event.target.value === "" ? null : event.target.value,
        };
      } else {
        return item;
      }
    });

    setNetworkData(trainee);
  };

  const handleReset = async () => {
    fetchNetworkData(
      rosterId,
      asdLeadOracleId,
      taskTypeId,
      scheduleType,
      agentOracleId,
      noRosterIdClassName,
      isNoRosterIdSearch,
    );
  };

  const handleSave = async () => {
    const hasChanges =
      JSON.stringify(NetworkData) !== JSON.stringify(originalNetworkData);

    if (!hasChanges) {
      setSnack({
        open: true,
        message: "No changes to save.",
        severity: "info",
      });
      return true;
    }
    setLoader(true);
    try {
      const data = createData();
      if (data.length !== NetworkData.length) return false;
      const response = await saveOMTNetwork(data, localStorage.getItem("User"));

      if (response?.message === "Success") {
        setSnack({
          open: true,
          message: "All changes have been saved Successfully.",
          severity: "success",
        });
        await fetchNetworkData(
          rosterId,
          asdLeadOracleId,
          taskTypeId,
          scheduleType,
          agentOracleId,
          noRosterIdClassName,
          isNoRosterIdSearch,
        );
        return true;
      }
      setSnack({ open: true, message: "Failed to Save..", severity: "error" });
      return false;
    } catch {
      setSnack({
        open: true,
        message: "Error saving Network..",
        severity: "error",
      });
      return false;
    } finally {
      setLoader(false);
    }
  };

  const createData = () => {
    const booleanFields = [
      "ISPGeolocationOutsideCountry",
      "OnWiFiLOBNotAllowed",
      "PacketLossLatency",
      "SIPALGEnabled",
      "NetworkSpeedDLFailed",
      "NetworkSpeedULFailed",
    ];

    return NetworkData.map((record) => ({
      OMTNetworkId: record.OMTNetworkId || 0,
      OMTClassDetailsId: record.OMTClassDetailsId,
      ClassRosterId: record.ClassRosterId,
      OracleId: record.OracleId,
      EmployeeName: record.EmployeeName,
      TraineeStatus: record.TraineeStatus,
      NetworkSpeedDLinMbps: record.NetworkSpeedDLinMbps || null,
      NetworkSpeedULinMbps: record.NetworkSpeedULinMbps || null,
      Comments: record.Comments,
      OMTScheduleStatusType: record.ScheduleType || "",
      ...Object.fromEntries(
        booleanFields.map((field) => [field, record[field] || false]),
      ),
    }));
  };

  const sortByCol = (data, col, direction) =>
    [...data].sort((a, b) => {
      if (typeof a[col] === "string" && typeof b[col] === "string") {
        return direction === "ASC"
          ? a[col].toLowerCase() > b[col].toLowerCase()
            ? 1
            : -1
          : a[col].toLowerCase() < b[col].toLowerCase()
            ? 1
            : -1;
      }
      return direction === "ASC"
        ? a[col] > b[col]
          ? 1
          : -1
        : a[col] < b[col]
          ? 1
          : -1;
    });

  const sorting = (col) => {
    if (order === "ASC") {
      setNetworkData(sortByCol(NetworkData, col, "ASC"));
      setOriginalNetworkData(sortByCol(originalNetworkData, col, "ASC"));
      setOrder("DSC");
    } else if (order === "DSC") {
      setNetworkData(sortByCol(NetworkData, col, "DSC"));
      setOriginalNetworkData(sortByCol(originalNetworkData, col, "DSC"));
      setOrder("ASC");
    }
  };

  const traineeStatusFilter = useColumnFilter(NetworkData, "TraineeStatus");

  const FilteredNetworkData = useMemo(() => {
    return NetworkData.map((row, originalIndex) => ({
      row,
      originalIndex,
    })).filter(({ row }) => traineeStatusFilter.matches(row));
  }, [NetworkData, traineeStatusFilter.selectedValues]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box>
        <Paper sx={{ padding: 2 }}>
          <TableContainer className={classes.container}>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ backgroundColor: "white" }}
                    onClick={() =>
                      sorting(
                        isUploadMode ? "NoRosterIdClassName" : "ClassRosterId",
                      )
                    }
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={isUploadMode ? "Class Name" : "RID"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Name")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Name"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Type")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Type"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Status")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Trainee Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <IconButton
                        onClick={traineeStatusFilter.openFilter}
                        size="small"
                        color={
                          traineeStatusFilter.selectedValues.length
                            ? "primary"
                            : "default"
                        }
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Down Speed Mbps"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Down Speed Fail"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Up Speed Mbps"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Upload Speed Fail"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"On Wi-Fi – LOB Not Allowed"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Packet Loss/Latency"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"ISP and Geolocation outside Country"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>

                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"SIP ALG Enabled"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Comments"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {FilteredNetworkData.map(({ row, originalIndex }) => {
                  const rowKey = `${row.OracleId}-${originalIndex}`;
                  const isDisabled =
                    row.AttendanceNoShow === true ||
                    row.AttendanceISPOutage === true || // disable if no show or ISP outage in attendance tab.
                    row.CredentialsPasswordNotWorking === true || // disable if Password Not Working in AD Credential tab.
                    row.CredentialsWindowsLoginFailure === true || // disable if Windows Login Failure in AD Credential tab.
                    isLockedByHardware(row); // disable if hardware issue unresolved
                  return (
                    <TableRow key={rowKey}>
                      <TableCell align="center">
                        <Grid container>
                          <Grid item xs={12}>
                            <TextLabel
                              text={
                                noRosterIdClassName
                                  ? row.NoRosterIdClassName
                                  : row.ClassRosterId
                              }
                              fontSize={14}
                              subHeading
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip title="Attach File">
                              <Badge
                                badgeContent={row?.AttachmentCount || 0}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    color: "#ffffff",
                                    backgroundColor: red[700],
                                  },
                                }}
                              >
                                <IconButton
                                  onClick={() => handleAttachmentOpen(row)}
                                  size="small"
                                  disableRipple
                                  disabled={isDisabled}
                                  sx={{
                                    backgroundColor: "#3299ff",
                                    borderRadius: 1,
                                  }}
                                  aria-label="attach file"
                                >
                                  <AttachFile
                                    fontSize="small"
                                    sx={{ color: "#ffffff" }}
                                  />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>{row.EmployeeName}</TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleType} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleStatus} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.TraineeStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TextField
                            size="small"
                            disabled={isDisabled}
                            sx={{ width: "68px" }}
                            value={
                              row.NetworkSpeedDLinMbps
                                ? row.NetworkSpeedDLinMbps
                                : ""
                            }
                            onInput={(e) => {
                              const newValue = e.target.value.replace(
                                /[^0-9.]/g,
                                "",
                              );

                              if (newValue.length <= 4) {
                                e.target.value = newValue;
                              } else {
                                e.target.value = newValue.slice(0, 4);
                              }

                              handleTextValueChange(
                                e,
                                originalIndex,
                                "NetworkSpeedDLinMbps",
                              );
                            }}
                            inputProps={{ maxLength: 4 }}
                            placeholder="Max 4 Characters"
                          />
                          {row.NetworkSpeedDLinMbps >= 15 ? (
                            <NetworkCheckOutlinedIcon
                              sx={{ color: "#2aa154" }}
                            />
                          ) : (
                            <WifiOffOutlinedIcon sx={{ color: "#C90370" }} />
                          )}
                        </Box>
                      </TableCell>
                      {/* need to update */}
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="NetworkSpeedDLFailed"
                          checked={row.NetworkSpeedDLFailed}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TextField
                            size="small"
                            disabled={isDisabled}
                            sx={{ width: "68px" }}
                            value={
                              row.NetworkSpeedULinMbps
                                ? row.NetworkSpeedULinMbps
                                : ""
                            }
                            onInput={(e) => {
                              const newValue = e.target.value.replace(
                                /[^0-9.]/g,
                                "",
                              );

                              if (newValue.length <= 4) {
                                e.target.value = newValue;
                              } else {
                                e.target.value = newValue.slice(0, 4);
                              }
                              handleTextValueChange(
                                e,
                                originalIndex,
                                "NetworkSpeedULinMbps",
                              );
                            }}
                            inputProps={{ maxLength: 4 }}
                            placeholder="Max 4"
                          />
                          {row.NetworkSpeedULinMbps >= 1.5 ? (
                            <NetworkCheckOutlinedIcon
                              sx={{ color: "#2aa154" }}
                            />
                          ) : (
                            <WifiOffOutlinedIcon sx={{ color: "#C90370" }} />
                          )}
                        </Box>
                      </TableCell>
                      {/* need to update */}
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="NetworkSpeedULFailed"
                          checked={row.NetworkSpeedULFailed}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="OnWiFiLOBNotAllowed"
                          checked={row.OnWiFiLOBNotAllowed}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="PacketLossLatency"
                          checked={row.PacketLossLatency}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="ISPGeolocationOutsideCountry"
                          checked={row.ISPGeolocationOutsideCountry}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="SIPALGEnabled"
                          checked={row.SIPALGEnabled}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.minWidth}>
                        <CustomTooltip title={row.Comments || ""}>
                          <TextField
                            size="small"
                            disabled={isDisabled}
                            multiline
                            fullWidth
                            maxRows={4}
                            value={row.Comments || ""}
                            maxLength={100}
                            onChange={(e) =>
                              handleTextValueChange(
                                e,
                                originalIndex,
                                "Comments",
                              )
                            }
                            InputProps={{ sx: { ...fontStyles } }}
                            helperText={`${row.Comments ? row.Comments.length : 0}/${100}`}
                            inputProps={{ maxLength: 100 }}
                            placeholder="Max 100 Characters"
                          />
                        </CustomTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ColumnFilterPopover
            anchorEl={traineeStatusFilter.anchorEl}
            onClose={traineeStatusFilter.closeFilter}
            options={traineeStatusFilter.uniqueValues}
            selectedValues={traineeStatusFilter.selectedValues}
            onToggle={traineeStatusFilter.toggleValue}
            onClear={traineeStatusFilter.clearFilter}
          />
          <Box className={classes.buttonBox}>
            <CustomButton
              variant="contained"
              className={classes.buttonSave}
              size="small"
              onClick={handleSave}
              disabled={!editAccess}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={classes.buttonReset}
              size="small"
              onClick={handleReset}
              disabled={!editAccess}
            >
              Reset
            </CustomButton>
          </Box>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset} />
      {openAttachmentDialog && (
        <OMTAttachments
          openAttachmentDialog={openAttachmentDialog}
          row={selectedRow}
          category="Network"
          onClose={() => setOpenAttachmentDialog(false)}
          editAccess={editAccess}
        />
      )}
    </>
  );
});

Network.propTypes = {
  rosterId: PropTypes.string,
  asdLeadOracleId: PropTypes.string,
  taskTypeId: PropTypes.string,
  scheduleType: PropTypes.string,
  editAccess: PropTypes.bool,
  agentOracleId: PropTypes.string,
  onDirtyChange: PropTypes.func,
};

export default Network;
